import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Typography,
  Row,
  Col,
  Statistic,
  Drawer,
  Descriptions,
  Badge,
  Divider,
  Alert,
  Tooltip,
  Avatar,
  Timeline,
  message,
} from 'antd';
import {
  AuditOutlined,
  SearchOutlined,
  UserOutlined,
  EyeOutlined,
  HistoryOutlined,
  FileTextOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { apiService } from '../../services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// 審計日誌數據類型定義
interface AuditLogEntry {
  id: string;
  timestamp: string;
  userId?: string;
  userName: string;
  userRole?: string;
  action: string;
  actionType: 'CREATE' | 'UPDATE' | 'DELETE' | 'VIEW' | 'APPROVE' | 'REJECT' | 'EXPORT' | 'IMPORT' | 'AUDIT' | 'LOGIN' | 'LOGOUT';
  module: string;
  entityType: string;
  entityId?: string;
  entityName?: string;
  description?: string;
  ipAddress?: string;
  userAgent?: string;
  changes?: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  metadata?: Record<string, any>;
  riskLevel: 'low' | 'medium' | 'high';
  status: 'success' | 'failed' | 'pending';
  errorMessage?: string;
  notes?: string;
  companyId?: string;
  createdAt: string;
}

// API 函數 - 使用統一的 apiService
const fetchAuditLogs = async (params: {
  page?: number;
  limit?: number;
  module?: string;
  actionType?: string;
  riskLevel?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  searchText?: string;
}) => {
  try {
    return await apiService.getAuditLogs(params);
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    throw error;
  }
};

const fetchAuditStatistics = async () => {
  try {
    return await apiService.getAuditStatistics();
  } catch (error) {
    console.error('Error fetching audit statistics:', error);
    throw error;
  }
};

const exportAuditLogs = async (params: {
  module?: string;
  actionType?: string;
  startDate?: string;
  endDate?: string;
  format?: string;
}) => {
  try {
    const blob = await apiService.exportAuditLogs({
      module: params.module,
      actionType: params.actionType,
      startDate: params.startDate,
      endDate: params.endDate,
    });
    
    if (params.format === 'csv') {
      // 下載 CSV 文件
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${dayjs().format('YYYY-MM-DD-HHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
    
    return blob;
  } catch (error) {
    console.error('Error exporting audit logs:', error);
    throw error;
  }
};

const AuditLog: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<AuditLogEntry | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  
  // 篩選狀態
  const [filters, setFilters] = useState({
    action: '',
    module: '',
    riskLevel: '',
    status: '',
    dateRange: null as [Dayjs, Dayjs] | null,
    searchText: '',
    userId: ''
  });

  // 用戶列表（用於篩選）
  const [userList, setUserList] = useState<{id: string; name: string; email: string}[]>([]);
  
  // 統計數據
  const [statistics, setStatistics] = useState({
    total: 0,
    success: 0,
    failed: 0,
    highRisk: 0,
  });

  // 加載審計日誌
  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.current,
        limit: pagination.pageSize,
      };
      
      if (filters.action) params.actionType = filters.action;
      if (filters.module) params.module = filters.module;
      if (filters.riskLevel) params.riskLevel = filters.riskLevel;
      if (filters.status) params.status = filters.status;
      if (filters.searchText) params.searchText = filters.searchText;
      if (filters.userId) params.userId = filters.userId;
      
      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].toISOString();
        params.endDate = filters.dateRange[1].toISOString();
      }
      
      const response: any = await fetchAuditLogs(params);
      
      if (response.success) {
        setAuditLogs(response.data);
        setPagination({
          ...pagination,
          total: response.pagination.total,
        });
      }
    } catch (error: any) {
      message.error('加載審計日誌失敗：' + (error.message || '未知錯誤'));
    } finally {
      setLoading(false);
    }
  };
  
  // 加載統計數據
  const loadStatistics = async () => {
    try {
      const response: any = await fetchAuditStatistics();
      if (response.success) {
        setStatistics({
          total: response.data.total,
          success: response.data.success,
          failed: response.data.failed,
          highRisk: response.data.highRisk,
        });
      }
    } catch (error: any) {
      console.error('加載統計數據失敗:', error);
    }
  };
  
  // 加載用戶列表（用於篩選）
  const loadUserList = async () => {
    try {
      const response: any = await apiService.getUsers({ limit: 500 });
      if (response.success && response.data) {
        const users = Array.isArray(response.data) ? response.data : (response.data.users || []);
        setUserList(users.map((u: any) => ({
          id: u.id,
          name: u.name || u.username || u.email,
          email: u.email
        })));
      }
    } catch (error) {
      console.error('加載用戶列表失敗:', error);
    }
  };

  // 初始加載
  useEffect(() => {
    loadAuditLogs();
    loadStatistics();
    loadUserList();
  }, []);
  
  // 篩選變化時重新加載
  useEffect(() => {
    loadAuditLogs();
  }, [filters, pagination.current, pagination.pageSize]);

  // 導出審計日誌
  const handleExport = async () => {
    try {
      message.loading('正在導出審計日誌...', 0);
      
      const params: any = {
        format: 'csv',
      };
      
      if (filters.action) params.actionType = filters.action;
      if (filters.module) params.module = filters.module;
      
      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].toISOString();
        params.endDate = filters.dateRange[1].toISOString();
      }
      
      await exportAuditLogs(params);
      message.destroy();
      message.success('審計日誌導出成功');
    } catch (error: any) {
      message.destroy();
      message.error('導出失敗：' + (error.message || '未知錯誤'));
    }
  };
  
  // 獲取動作類型圖標
  const getActionIcon = (actionType: string) => {
    const type = actionType.toUpperCase();
    switch (type) {
      case 'CREATE':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'UPDATE':
        return <ExclamationCircleOutlined style={{ color: '#1890ff' }} />;
      case 'DELETE':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'VIEW':
        return <EyeOutlined style={{ color: '#722ed1' }} />;
      case 'APPROVE':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'REJECT':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'EXPORT':
        return <DownloadOutlined style={{ color: '#fa8c16' }} />;
      case 'IMPORT':
        return <DownloadOutlined style={{ color: '#13c2c2' }} />;
      default:
        return <FileTextOutlined />;
    }
  };

  // 獲取風險等級顏色
  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high':
        return 'red';
      case 'medium':
        return 'orange';
      case 'low':
        return 'green';
      default:
        return 'default';
    }
  };

  // 獲取狀態標籤顏色
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // 表格列定義
  const columns: ColumnsType<AuditLogEntry> = [
    {
      title: '時間',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      sorter: (a, b) => dayjs(a.timestamp).unix() - dayjs(b.timestamp).unix(),
      render: (timestamp) => (
        <div>
          <div>{dayjs(timestamp).format('YYYY-MM-DD')}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(timestamp).format('HH:mm:ss')}
          </Text>
        </div>
      ),
    },
    {
      title: '用戶',
      key: 'user',
      width: 120,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 8 }} />
          <div>
            <div style={{ fontWeight: 500 }}>{record.userName}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.userRole}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: '動作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          {getActionIcon(record.actionType)}
          <span>{record.action}</span>
        </Space>
      ),
    },
    {
      title: '模組',
      dataIndex: 'module',
      key: 'module',
      width: 200,
    },
    {
      title: '實體',
      key: 'entity',
      width: 300,
      render: (_, record) => {
        // 檢查 entityName 中是否包含請求數據
        const hasRequestData = record.entityName?.includes('📋 請求數據:');
        const lines = record.entityName?.split('\n') || [];
        
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {lines[0] || record.entityName}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.entityType}: {record.entityId}
            </Text>
            {hasRequestData && lines.length > 1 && (
              <Tooltip 
                title={
                  <pre style={{ 
                    maxWidth: '600px', 
                    overflow: 'auto',
                    fontSize: '11px',
                    margin: 0
                  }}>
                    {lines.slice(1).join('\n')}
                  </pre>
                }
                placement="topLeft"
              >
                <div style={{ 
                  marginTop: '4px',
                  color: '#1890ff',
                  fontSize: '11px',
                  cursor: 'pointer'
                }}>
                  📋 查看完整請求數據 →
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: {
        showTitle: false,
      },
      render: (description) => (
        <Tooltip placement="topLeft" title={description}>
          {description}
        </Tooltip>
      ),
    },
    {
      title: '風險等級',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 90,
      render: (riskLevel) => (
        <Tag color={getRiskLevelColor(riskLevel)}>
          {riskLevel === 'high' ? '高風險' : riskLevel === 'medium' ? '中風險' : '低風險'}
        </Tag>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Badge
          status={getStatusColor(status) as any}
          text={status === 'success' ? '成功' : status === 'failed' ? '失敗' : '處理中'}
        />
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedRecord(record);
            setDrawerVisible(true);
          }}
        >
          詳情
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <AuditOutlined style={{ marginRight: 8 }} />
        審計日誌
      </Title>
      
      <Alert
        message="審計追蹤"
        description="此頁面顯示系統中所有用戶操作的詳細記錄，包括創建、修改、刪除等操作，用於合規性檢查和安全審計。"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* 統計卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="總記錄數"
              value={statistics.total}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="成功操作"
              value={statistics.success}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="失敗操作"
              value={statistics.failed}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="高風險操作"
              value={statistics.highRisk}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#d48806' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 篩選區域 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={18}>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadAuditLogs();
                  loadStatistics();
                }}
              >
                刷新
              </Button>
              {/*<Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
              >
                導出審計日誌
              </Button>*/}
            </Space>
          </Col>
        </Row>
        <Row gutter={[16, 12]}>
          <Col xs={24} sm={12} md={6} lg={5}>
            <Input
              placeholder="搜尋描述..."
              prefix={<SearchOutlined />}
              value={filters.searchText}
              onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              placeholder="用戶"
              style={{ width: '100%' }}
              value={filters.userId || undefined}
              onChange={(value) => setFilters({ ...filters, userId: value || '' })}
              allowClear
              showSearch
              // Search the label, not the children. Interleaving expressions
              // with literal text makes the children the array
              // ['Ada', ' (', 'ada@x.com', ')'] rather than a string, and the
              // old filter called toLowerCase() straight on it — which threw on
              // every keystroke and took the whole page down with it.
              optionFilterProp="label"
            >
              {userList.map(user => (
                <Option key={user.id} value={user.id} label={`${user.name} (${user.email})`}>
                  {user.name} ({user.email})
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={3}>
            <Select
              placeholder="動作類型"
              style={{ width: '100%' }}
              value={filters.action}
              onChange={(value) => setFilters({ ...filters, action: value })}
              allowClear
            >
              <Option value="CREATE">創建</Option>
              <Option value="UPDATE">更新</Option>
              <Option value="DELETE">刪除</Option>
              <Option value="VIEW">查看</Option>
              <Option value="APPROVE">批準</Option>
              <Option value="REJECT">拒絕</Option>
              <Option value="EXPORT">導出</Option>
              <Option value="IMPORT">導入</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              placeholder="模組"
              style={{ width: '100%' }}
              value={filters.module}
              onChange={(value) => setFilters({ ...filters, module: value })}
              allowClear
            >
              <Option value="Products">產品管理</Option>
              <Option value="Stock Management">庫存管理</Option>
              <Option value="Inbound">入庫管理</Option>
              <Option value="Outbound">出庫管理</Option>
              <Option value="Picking Management">揀貨管理</Option>
              <Option value="Internal Transfer">內部調撥</Option>
              <Option value="Return Management">退貨管理</Option>
              <Option value="Warehouse Management">倉庫管理</Option>
              <Option value="Sales Order">銷售訂單</Option>
              <Option value="Purchase Order">採購訂單</Option>
              <Option value="Customer">客戶管理</Option>
              <Option value="Vendor">供應商管理</Option>
              <Option value="Settings">系統設置</Option>
              <Option value="User Management">用戶管理</Option>
              <Option value="Authentication">身份驗證</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={3}>
            <Select
              placeholder="風險等級"
              style={{ width: '100%' }}
              value={filters.riskLevel}
              onChange={(value) => setFilters({ ...filters, riskLevel: value })}
              allowClear
            >
              <Option value="high">高風險</Option>
              <Option value="medium">中風險</Option>
              <Option value="low">低風險</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={5}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates as [Dayjs, Dayjs] | null })}
              placeholder={['開始日期', '結束日期']}
            />
          </Col>
        </Row>
      </Card>

      {/* 審計日誌表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={auditLogs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) =>
              `顯示 ${range[0]}-${range[1]} 項，共 ${total} 項記錄`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 詳情抽屜 */}
      <Drawer
        title="審計日誌詳情"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={600}
      >
        {selectedRecord && (
          <div>
            <Descriptions title="基本信息" column={1} bordered>
              <Descriptions.Item label="時間">
                {dayjs(selectedRecord.timestamp).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="用戶">
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  {selectedRecord.userName} ({selectedRecord.userRole})
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="動作">
                <Space>
                  {getActionIcon(selectedRecord.actionType)}
                  {selectedRecord.action}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="模組">
                {selectedRecord.module}
              </Descriptions.Item>
              <Descriptions.Item label="實體">
                {selectedRecord.entityType}: {selectedRecord.entityId}
              </Descriptions.Item>
              <Descriptions.Item label="實體名稱">
                {selectedRecord.entityName}
              </Descriptions.Item>
              <Descriptions.Item label="風險等級">
                <Tag color={getRiskLevelColor(selectedRecord.riskLevel)}>
                  {selectedRecord.riskLevel === 'high' ? '高風險' : 
                   selectedRecord.riskLevel === 'medium' ? '中風險' : '低風險'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="狀態">
                <Badge
                  status={getStatusColor(selectedRecord.status) as any}
                  text={selectedRecord.status === 'success' ? '成功' : 
                        selectedRecord.status === 'failed' ? '失敗' : '處理中'}
                />
              </Descriptions.Item>
              <Descriptions.Item label="IP地址">
                {selectedRecord.ipAddress}
              </Descriptions.Item>
              <Descriptions.Item label="用戶代理">
                <Text ellipsis={{ tooltip: selectedRecord.userAgent }}>
                  {selectedRecord.userAgent}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Title level={4}>描述</Title>
            <Text>{selectedRecord.description}</Text>

            {selectedRecord.changes && selectedRecord.changes.length > 0 && (
              <>
                <Divider />
                <Title level={4}>變更記錄</Title>
                <Timeline>
                  {selectedRecord.changes.map((change, index) => (
                    <Timeline.Item key={index}>
                      <div>
                        <Text strong>{change.field}: </Text>
                        <Text delete type="secondary">
                          {change.oldValue || '(空)'}
                        </Text>
                        <Text> → </Text>
                        <Text strong style={{ color: '#52c41a' }}>
                          {change.newValue || '(空)'}
                        </Text>
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </>
            )}

            {selectedRecord.metadata && Object.keys(selectedRecord.metadata).length > 0 && (
              <>
                <Divider />
                <Title level={4}>元數據</Title>
                <Descriptions column={1} size="small">
                  {Object.entries(selectedRecord.metadata).map(([key, value]) => {
                    // 如果是 requestBody，格式化顯示
                    if (key === 'requestBody' && typeof value === 'object' && value !== null) {
                      return (
                        <Descriptions.Item key={key} label="📋 完整請求數據">
                          <pre style={{ 
                            background: '#f5f5f5', 
                            padding: '12px', 
                            borderRadius: '4px', 
                            overflow: 'auto',
                            maxHeight: '400px',
                            fontSize: '12px'
                          }}>
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        </Descriptions.Item>
                      );
                    }
                    
                    // 如果是 requestParams，格式化顯示
                    if (key === 'requestParams' && typeof value === 'object' && value !== null) {
                      return (
                        <Descriptions.Item key={key} label="🔗 請求參數">
                          <pre style={{ 
                            background: '#f5f5f5', 
                            padding: '12px', 
                            borderRadius: '4px', 
                            overflow: 'auto',
                            fontSize: '12px'
                          }}>
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        </Descriptions.Item>
                      );
                    }
                    
                    // 其他元數據正常顯示
                    return (
                      <Descriptions.Item key={key} label={key}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Descriptions.Item>
                    );
                  })}
                </Descriptions>
              </>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default AuditLog;